import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  Row,
  Col,
  Label,
  Input,
  Button,
  FormFeedback,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { toast } from "react-toastify";
import { addNewAccount } from "../../slices/accounts/thunk";
import { resetAccountFlags } from "../../slices/accounts/reducer";
import { getPhases } from "../../slices/phases/thunk";

const selectAccountState = createSelector(
  (state) => state.Accounts,
  (accounts) => ({
    isLoading: accounts.isLoading || false,
    isAccountCreated: accounts.isAccountCreated || false,
    error: accounts.error,
  })
);

const selectPhaseState = createSelector(
  (state) => state.Phases,
  (phase) => ({
    phases: phase.phases || [],
    isPhaseSuccess: phase.isPhaseSuccess || false,
    phaseError: phase.error,
  })
);

const AccountCreationModal = ({ isOpen, toggle }) => {
  const dispatch = useDispatch();
  const { isLoading, isAccountCreated, error } = useSelector(selectAccountState);
  const { phases, isPhaseSuccess, phaseError } = useSelector(selectPhaseState);

  const [formData, setFormData] = useState({
    cif: "",
    email: "",
    token: "",
    phase: "",
    series: ""
  });

  const [validation, setValidation] = useState({
    cif: "",
    email: "",
    token: "",
    series: ""
  });

  useEffect(() => {
    if (isOpen && phases.length === 0) {
      dispatch(getPhases());
    }
  }, [isOpen, phases.length, dispatch]);

  useEffect(() => {
    if (isAccountCreated) {
      setFormData({
        cif: "",
        email: "",
        token: "",
        phase: "",
        series: ""
      });
      setValidation({
        cif: "",
        email: "",
        token: "",
        series: ""
      });
      toggle();
      dispatch(resetAccountFlags());
    }
  }, [isAccountCreated, toggle, dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validation[name]) {
      setValidation((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.cif.trim()) {
      errors.cif = "CIF is required";
    } else if (formData.cif.length < 3) {
      errors.cif = "CIF must be at least 3 characters";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = "Please enter a valid email address";
      }
    }

    if (!formData.token.trim()) {
      errors.token = "Token is required";
    } else if (formData.token.length < 10) {
      errors.token = "Token must be at least 10 characters";
    }

    if (!formData.series.trim()) {
      errors.series = "Series is required";
    }

    setValidation(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the validation errors", { autoClose: 3000 });
      return;
    }

    try {
      const cleanedData = {
        cif: formData.cif.trim(),
        email: formData.email.trim().toLowerCase(),
        token: formData.token.trim(),
        phase: formData.phase.trim() || null,
        series: formData.series.trim() || null
      };
      await dispatch(addNewAccount(cleanedData)).unwrap();
    } catch (error) {
      console.error("Account creation failed:", error);
    }
  };

  const handleCancel = () => {
    setFormData({
      cif: "",
      email: "",
      token: "",
      phase: null,
      series: ""
    });
    setValidation({
      cif: "",
      email: "",
      token: "",
      series: ""
    });
    dispatch(resetAccountFlags());
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleCancel} size="lg" centered>
      <ModalHeader toggle={handleCancel}>
        <i className="ri-file-add-line me-2 text-success"></i>
        Add SmartBill Account
      </ModalHeader>

      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label for="cif" className="form-label">
                  SmartBill CIF <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="cif"
                  name="cif"
                  value={formData.cif}
                  onChange={handleInputChange}
                  invalid={!!validation.cif}
                  placeholder="Enter CIF"
                  disabled={isLoading}
                />
                {validation.cif && <FormFeedback>{validation.cif}</FormFeedback>}
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <Label for="email" className="form-label">
                  SmartBill Email <span className="text-danger">*</span>
                </Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  invalid={!!validation.email}
                  placeholder="Enter email address"
                  disabled={isLoading}
                />
                {validation.email && <FormFeedback>{validation.email}</FormFeedback>}
              </div>
            </Col>

            <Col md={12}>
              <div className="mb-3">
                <Label for="token" className="form-label">
                  SmartBill API Token <span className="text-danger">*</span>
                </Label>
                <div className="input-group">
                  <Input
                    type="text"
                    id="token"
                    name="token"
                    value={formData.token}
                    onChange={handleInputChange}
                    invalid={!!validation.token}
                    placeholder="Enter token"
                    disabled={isLoading}
                  />
                </div>
                {validation.token && <FormFeedback style={{ display: 'block' }}>{validation.token}</FormFeedback>}
                <div className="form-text">
                  Paste the API token generated in your SmartBill account (min 10 chars).
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <Label for="phase" className="form-label">
                  Phase (link to inventory)
                </Label>
                <Input
                  type="select"
                  id="phase"
                  name="phase"
                  value={formData.phase}
                  onChange={handleInputChange}
                  disabled={isLoading}
                >
                  <option value="">Select Phase (Optional)</option>
                  {isPhaseSuccess && phases.length > 0 ? (
                    phases.map((phase) => (
                      <option key={phase.id} value={phase.id}>
                        {phase.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      {phaseError ? "Error loading phases" : "Loading phases..."}
                    </option>
                  )}
                </Input>
                <div className="form-text">
                  {phaseError ? (
                    <span className="text-danger">
                      Error loading phases. You can create the account and set the Phase later.
                    </span>
                  ) : (
                    "When set, this account will be used for invoices of units in the selected Phase."
                  )}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label for="series" className="form-label">
                  SmartBill Series <span className="text-danger">*</span>
                </Label>
                <div className="input-group">
                  <Input
                    type="text"
                    id="series"
                    name="series"
                    value={formData.series}
                    onChange={handleInputChange}
                    invalid={!!validation.series}
                    placeholder="Enter series"
                    disabled={isLoading}
                  />
                </div>
                {validation.series && <FormFeedback style={{ display: 'block' }}>{validation.series}</FormFeedback>}
              </div>
            </Col>
          </Row>

          {/* Preview Section */}
          <div className="border-top pt-3 mt-3">
            <h6 className="mb-3">Preview</h6>
            <Row className="g-2">
              <Col md={3}>
                <small className="text-muted">SmartBill CIF:</small>
                <div className="fw-medium">{formData.cif || "Not set"}</div>
              </Col>
              <Col md={3}>
                <small className="text-muted">SmartBill Email:</small>
                <div className="text-break">{formData.email || "Not set"}</div>
              </Col>
              <Col md={3}>
                <small className="text-muted">Phase:</small>
                <div>
                  <span className="badge bg-soft-info text-info">
                    {formData.phase || "No phase"}
                  </span>
                </div>
              </Col>
              <Col md={3}>
                <small className="text-muted">API Token:</small>
                <div className="text-break">
                  <code className="small">
                    {formData.token ? `${formData.token.substring(0, 8)}...` : "Not set"}
                  </code>
                </div>
              </Col>
            </Row>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            color="light"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            color="success"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Creating...
              </>
            ) : (
              <>
                <i className="ri-add-line me-1"></i>
                Add SmartBill Account
              </>
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AccountCreationModal;
